import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /** When true, options are treated as server-filtered; dropdown shows all of `options` without local label filtering. */
  skipLocalFilter?: boolean;
  /** Fired when the search text changes (open dropdown typing). Useful to drive debounced API search. */
  onSearchTermChange?: (term: string) => void;
  /** Fired with the selected option object, or null when cleared. */
  onOptionSelect?: (option: Option | null) => void;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = 'Search...',
  required = false,
  error,
  disabled = false,
  skipLocalFilter = false,
  onSearchTermChange,
  onOptionSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const selectedLabel = selectedOption?.label || '';

  const filteredOptions = skipLocalFilter
    ? options
    : options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  const onSearchTermChangeRef = useRef(onSearchTermChange);
  onSearchTermChangeRef.current = onSearchTermChange;
  useLayoutEffect(() => {
    if (!skipLocalFilter || !onSearchTermChangeRef.current) return;
    onSearchTermChangeRef.current(searchTerm);
  }, [searchTerm, skipLocalFilter]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleSelect = (optionValue: string) => {
    const option = options.find((candidate) => candidate.value === optionValue) || null;
    onChange(optionValue);
    onOptionSelect?.(option);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    onOptionSelect?.(null);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <div className={label ? 'space-y-1' : ''} ref={containerRef}>
      {label ? (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      ) : null}

      <div className="relative">
        {/* Selected value display / Search input */}
        <div
          onClick={handleOpen}
          className={`
            w-full px-3 py-2
            bg-white dark:bg-[#0f172a]
            border rounded-lg
            text-gray-900 dark:text-white
            cursor-pointer
            flex items-center justify-between
            ${error
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-300 dark:border-gray-600'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'}
          `}
        >
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1 bg-transparent outline-none"
              disabled={disabled}
            />
          ) : (
            <span className={selectedLabel ? '' : 'text-gray-400 dark:text-gray-500'}>
              {selectedLabel || `Select ${label}`}
            </span>
          )}

          <div className="flex items-center gap-2">
            {selectedLabel && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              <ul className="py-1">
                {filteredOptions.map((option, index) => (
                  <li
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={`
                      px-3 py-2 cursor-pointer
                      ${index === highlightedIndex
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#334155]'
                      }
                    `}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.label}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>No results found</p>
                {searchTerm && (
                  <p className="mt-1">Try searching for something else</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-500 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};
